/**
 * DataInspector Component
 *
 * A side panel for inspecting workflow execution data with:
 * - JSON tree view for input/output
 * - Search within JSON
 * - Pin nodes for comparison
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  TabList,
  Tab,
  Text,
  Badge,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
  SearchRegular,
  PinRegular,
  PinOffRegular,
  ArrowMinimizeRegular,
} from '@fluentui/react-icons';
import { JsonTreeView } from './JsonTreeView';
import type { NodeExecution, RequestTriggerContent, RequestContentSide, RequestContentRetryTarget } from '@/types/monitoring';
import { RequestContent } from '@/components/MonitoringView/shared/RequestContent';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    width: '350px',
    minWidth: '280px',
    maxWidth: '500px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    flex: 1,
  },
  searchContainer: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  searchInput: {
    width: '100%',
  },
  tabList: {
    paddingLeft: tokens.spacingHorizontalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingHorizontalM,
  },
  nodeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  nodeName: {
    fontWeight: tokens.fontWeightSemibold,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  // Compare view
  compareContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  compareCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  compareCardHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalXS,
  },
  compareCardContent: {
    maxHeight: '200px',
    overflow: 'auto',
    padding: tokens.spacingHorizontalS,
  },
  statusBadge: {
    marginLeft: 'auto',
  },
  compareBadge: {
    marginLeft: tokens.spacingHorizontalXS,
  },
});

type InspectorTab = 'input' | 'output' | 'compare';

type PinnedNode = NodeExecution & { requestDetailsQueryKey?: QueryKey | undefined };

/** Observe only the original pending query; never fetch or make settled pins live. */
function PendingRequestPin({ queryKey, onResolved }: {
  queryKey: QueryKey;
  onResolved: (content: RequestTriggerContent) => void;
}) {
  const { data, isError } = useQuery<{ requestContent?: RequestTriggerContent }>({ queryKey, enabled: false });
  useEffect(() => {
    if (data?.requestContent) onResolved(data.requestContent);
    else if (isError) onResolved({
      input: { status: 'error', reason: 'retrieval', retryable: true },
      output: { status: 'error', reason: 'retrieval', retryable: true },
    });
  }, [data, isError, onResolved]);
  return null;
}

export interface DataInspectorProps {
  /** Currently selected node execution data */
  nodeExecution: NodeExecution | null;
  requestDetailsQueryKey?: QueryKey | undefined;
  onRetryRequest?: ((side: RequestContentSide) => void) | undefined;
  retryingRequestSide?: RequestContentRetryTarget | undefined;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Callback when minimize button is clicked */
  onMinimize?: () => void;
}

export const DataInspector = ({
  nodeExecution,
  requestDetailsQueryKey,
  onRetryRequest,
  retryingRequestSide,
  onClose,
  onMinimize,
}: DataInspectorProps) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<InspectorTab>('input');
  const [searchQuery, setSearchQuery] = useState('');
  const [pinnedNodes, setPinnedNodes] = useState<PinnedNode[]>([]);

  const resolvePendingPin = useCallback((pin: PinnedNode, content: RequestTriggerContent) => {
    setPinnedNodes(previous => {
      if (!previous.includes(pin) || !pin.requestContent) return previous;
      const input = pin.requestContent.input.status === 'loading' ? content.input : pin.requestContent.input;
      const output = pin.requestContent.output.status === 'loading' ? content.output : pin.requestContent.output;
      if (input === pin.requestContent.input && output === pin.requestContent.output) return previous;
      return previous.map(node => node === pin ? {
        ...pin,
        requestContent: { input, output },
        input: input.status === 'available' ? input.data : undefined,
        output: output.status === 'available' ? output.data : undefined,
      } : node);
    });
  }, []);

  const handlePin = useCallback(() => {
    if (!nodeExecution) return;

    setPinnedNodes(prev => {
      const isAlreadyPinned = prev.some(n => n.nodeId === nodeExecution.nodeId);
      if (isAlreadyPinned) {
        return prev.filter(n => n.nodeId !== nodeExecution.nodeId);
      }
      // Limit to 3 pinned nodes
      const pin = { ...nodeExecution, requestDetailsQueryKey };
      if (prev.length >= 3) {
        return [...prev.slice(1), pin];
      }
      return [...prev, pin];
    });
  }, [nodeExecution, requestDetailsQueryKey]);

  const handleUnpin = useCallback((nodeId: string) => {
    setPinnedNodes(prev => prev.filter(n => n.nodeId !== nodeId));
  }, []);

  const isPinned = useMemo(() => {
    if (!nodeExecution) return false;
    return pinnedNodes.some(n => n.nodeId === nodeExecution.nodeId);
  }, [nodeExecution, pinnedNodes]);

  const getStatusColor = (status: string): 'success' | 'danger' | 'warning' | 'informative' => {
    switch (status) {
      case 'succeeded': return 'success';
      case 'failed': return 'danger';
      case 'skipped': return 'warning';
      default: return 'informative';
    }
  };

  const renderContent = () => {
    if (!nodeExecution) {
      return (
        <div className={styles.emptyState}>
          <Text size={400}>No node selected</Text>
          <Text size={200}>Select a node in the canvas to inspect its data</Text>
        </div>
      );
    }

    if (nodeExecution.requestContent && (activeTab === 'input' || activeTab === 'output')) {
      const content = nodeExecution.requestContent[activeTab];
      return (
        <RequestContent content={content} label={activeTab === 'input' ? 'Input' : 'Output'} onRetry={onRetryRequest} retryingSide={retryingRequestSide}>
          {content.status === 'available' && (
            <JsonTreeView data={content.data} nullAsValue searchQuery={searchQuery} defaultExpandedDepth={2} />
          )}
        </RequestContent>
      );
    }

    switch (activeTab) {
      case 'input':
        return nodeExecution.input ? (
          <JsonTreeView
            data={nodeExecution.input}
            searchQuery={searchQuery}
            defaultExpandedDepth={2}
          />
        ) : (
          <div className={styles.emptyState}>
            <Text>No input data</Text>
          </div>
        );

      case 'output':
        return nodeExecution.output ? (
          <JsonTreeView
            data={nodeExecution.output}
            searchQuery={searchQuery}
            defaultExpandedDepth={2}
          />
        ) : (
          <div className={styles.emptyState}>
            <Text>No output data</Text>
          </div>
        );

      case 'compare':
        if (pinnedNodes.length === 0) {
          return (
            <div className={styles.emptyState}>
              <Text size={400}>No pinned nodes</Text>
              <Text size={200}>Pin nodes to compare their data side by side</Text>
            </div>
          );
        }
        return (
          <div className={styles.compareContainer}>
            {pinnedNodes.map(node => (
              <div key={node.nodeId} className={styles.compareCard}>
                <div className={styles.compareCardHeader}>
                  <Text weight="semibold" size={200}>{node.nodeName}</Text>
                  <Badge
                    className={styles.statusBadge}
                    appearance="filled"
                    color={getStatusColor(node.status)}
                    size="small"
                  >
                    {node.status}
                  </Badge>
                  <Tooltip content="Unpin" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<PinOffRegular />}
                      onClick={() => handleUnpin(node.nodeId)}
                    />
                  </Tooltip>
                </div>
                <div className={styles.compareCardContent}>
                  <Text size={200} weight="semibold">Output:</Text>
                  {node.requestContent && node.requestContent.output.status !== 'available' ? (
                    <RequestContent content={node.requestContent.output} label="Output" />
                  ) : <JsonTreeView
                    data={node.requestContent?.output.status === 'available' ? node.requestContent.output.data : node.output}
                    nullAsValue={!!node.requestContent}
                    searchQuery={searchQuery}
                    defaultExpandedDepth={1}
                  />}
                </div>
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className={styles.container}>
      {pinnedNodes.map(pin => pin.requestDetailsQueryKey && pin.requestContent
        && (pin.requestContent.input.status === 'loading' || pin.requestContent.output.status === 'loading') ? (
          <PendingRequestPin
            key={pin.nodeId}
            queryKey={pin.requestDetailsQueryKey}
            onResolved={content => resolvePendingPin(pin, content)}
          />
        ) : null)}
      {/* Header */}
      <div className={styles.header}>
        <Text className={styles.headerTitle}>Data Inspector</Text>
        {onMinimize && (
          <Tooltip content="Minimize" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowMinimizeRegular />}
              onClick={onMinimize}
            />
          </Tooltip>
        )}
        <Tooltip content="Close" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={onClose}
          />
        </Tooltip>
      </div>

      {/* Node info bar */}
      {nodeExecution && (
        <div className={styles.nodeInfo}>
          <Text className={styles.nodeName}>{nodeExecution.nodeName}</Text>
          <Badge
            appearance="filled"
            color={getStatusColor(nodeExecution.status)}
            size="small"
          >
            {nodeExecution.status}
          </Badge>
          <Tooltip content={isPinned ? 'Unpin node' : 'Pin for comparison'} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={isPinned ? <PinOffRegular /> : <PinRegular />}
              onClick={handlePin}
            />
          </Tooltip>
        </div>
      )}

      {/* Search */}
      <div className={styles.searchContainer}>
        <Input
          className={styles.searchInput}
          placeholder="Search in JSON..."
          contentBefore={<SearchRegular />}
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
          size="small"
        />
      </div>

      {/* Tabs */}
      <TabList
        className={styles.tabList}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as InspectorTab)}
        size="small"
      >
        <Tab value="input">Input</Tab>
        <Tab value="output">Output</Tab>
        <Tab value="compare">
          Compare
          {pinnedNodes.length > 0 && (
            <Badge appearance="filled" size="small" color="informative" className={styles.compareBadge}>
              {pinnedNodes.length}
            </Badge>
          )}
        </Tab>
      </TabList>

      {/* Content */}
      <div className={styles.content}>
        {renderContent()}
      </div>
    </div>
  );
};

DataInspector.displayName = 'DataInspector';
